import Controller, { OnAfterNavigate, OnBeforeSubmit, OnChange, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { AutocompleteAPI, CheckboxAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, RadioGroupAPI, SelectableItem, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import { ChangeEvent, MountEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, ModelService } from '@philips-internal/oswald-ui-core/lib/services';
import { Inject } from '@odinjs/odin';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';

import BaseController from '../../../BaseController';
import { Endpoint } from '../../../../constants/endpoints';
import { FragmentNames, HealthHistorySocialHistoryCreateAliases } from '../../../../codegen/index';
import { HealthHistoryListContext } from '../../service/health-history-list-service';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { DomainType, HealthHistorySocialHistoryResponse, HealthHistoryType, SearchType } from '../../health-history-types';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { YesNoType } from '../../../../utils/enum-utils';
import DialogService from '../../../../services/dialog-service';

interface ClassificationDomain {
  description: string;
  id: string;
  valueDomain: string;
}

enum WizardSteps {
  STEP_ONE = 'stepOne',
  STEP_TWO = 'stepTwo',
  STEP_THREE = 'stepThree'
}

@Controller({ component: HealthHistorySocialHistoryCreateAliases.HEALTH_HISTORY_SOCIAL_HISTORY_ADD_LAYOUT, fragment: FragmentNames.HEALTH_HISTORY_SOCIAL_HISTORY_CREATE })
class HealthHistorySocialHistortCreateController extends BaseController {

  @Inject(HealthHistorySocialHistoryCreateAliases.FORM_ONE)
  private formOneSocialHistoryCreateAPI!: FormAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.FORM_TWO)
  private formTwoSocialHistoryCreateAPI!: FormAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.FORM_THREE)
  private formThreeSocialHistoryCreateAPI!: FormAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.WIZARD)
  private wizardAPI!: WizardAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.CHECKBOX_DISPLAY_PATIENT_ALERTS)
  private checkboxAlertsAPI!: CheckboxAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.AUTOCOMPLETE_CLASSIFICATION_DOMAIN)
  private classificationAutoCompleteAPI!: AutocompleteAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.AUTOCOMPLETE_TYPE_SEARCH)
  private typeSearchAPI!: AutocompleteAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.FETCH_TYPE_SEARCH)
  private typeFetchSearchAPI!: FetchAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.AUTOCOMPLETE_BRAND_SEARCH)
  private brandSearchAPI!: AutocompleteAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.FETCH_BRAND_SEARCH)
  private brandFetchSearchAPI!: FetchAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.AUTOCOMPLETE_UNIT_MEASURE_SEARCH)
  private unitMeasureSearchAPI!: AutocompleteAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.FETCH_UNIT_MEASURE_SEARCH)
  private unitMeasureFetchSearchAPI!: FetchAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.INPUT_QUANTITY)
  private inputQuantityApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.INPUT_DESCRIPTIVE_QUANTITY)
  private inputDescriptiveQuantityApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.AUTOCOMPLETE_FREQUENCY_DOMAIN)
  private frequencyAutoCompleteAPI!: AutocompleteAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.INPUT_AGE_EXPERIENCE)
  private inputAgeExperienceApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.INPUT_AGE_REGULAR_USE)
  private inputAgeRegularUseApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.INPUT_TOTAL_PACKS)
  private inputTotalPacksApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.DATE_FIELD_START_DATE)
  private startDateInputApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.DATE_FIELD_END_DATE)
  private endDateInputApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.TEXT_AREA_CONSEQUENCES)
  private textAreaConsequencesApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.TEXT_AREA_FAMILY_ANTECEDENTS)
  private textAreaFamilyAntecedentsApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.FIELD_END_DATE)
  private fieldEndDateInputApi!: FieldAPI;

  @Inject(HealthHistorySocialHistoryCreateAliases.FIELD_START_DATE)
  private fieldStartDateInputApi!: FieldAPI;

  @OnValidate(HealthHistorySocialHistoryCreateAliases.FORM_ONE)
  handleOnValidate(validation: ValidateEvent): void {
    this.setStepDone(validation.valid, WizardSteps.STEP_ONE)
  }

  @OnValidate(HealthHistorySocialHistoryCreateAliases.FORM_TWO)
  handleOnSecondValidate(validationFormTwo: ValidateEvent): void {
    this.setStepDone(validationFormTwo.valid, WizardSteps.STEP_TWO)
  }

  @OnValidate(HealthHistorySocialHistoryCreateAliases.FORM_THREE)
  handleOnTirdValidate(validationFormThree: ValidateEvent): void {
    this.setStepDone(validationFormThree.valid, WizardSteps.STEP_THREE)
  }

  @OnAfterNavigate(HealthHistorySocialHistoryCreateAliases.WIZARD)
  handleOnMountWizardStepTwo(): void {
    this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_TWO, true);
  }

  private setStepDone(conditionToSetStepDone: boolean, step: WizardSteps): void {
    if (conditionToSetStepDone) {
      this.wizardAPI.setStepDone(step, true);
    }
  }

  @OnMount(HealthHistorySocialHistoryCreateAliases.RADIO_GROUP_CONDITION)
  handleMountRadioCondition(event: MountEvent): void {
    const radioGroupCondition = 'condition';
    const conditionExpression = 337373;
    const radioGroupAPI = event.target as RadioGroupAPI;
    this.scaleRadioGroup(conditionExpression, radioGroupAPI, radioGroupCondition)
  }

  @OnMount(HealthHistorySocialHistoryCreateAliases.CHECKBOX_DISPLAY_PATIENT_ALERTS)
  handleCheckDisplayPatient(event: MountEvent): void {
    const checkoxApi = event.target as CheckboxAPI;
    checkoxApi.setValue(true);
    this.checkboxAlertsAPI.setValue(true);
  }

  @OnMount({ target: HealthHistorySocialHistoryCreateAliases.WIZARD })
  handleFormMount(): void {
    HealthHistoryListContext.currentTab = HealthHistoryType.SOCIAL_HISTORY
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, patientId });
  }

  private deniesHabits(): void {
    this.classificationAutoCompleteAPI.setValue(undefined);
    this.typeSearchAPI.setValue(undefined);
    this.brandSearchAPI.setValue(undefined);
    this.unitMeasureSearchAPI.setValue(undefined);
    this.inputQuantityApi.setValue('');
    this.inputDescriptiveQuantityApi.setValue('');
    this.frequencyAutoCompleteAPI.setValue(undefined);
    this.inputAgeExperienceApi.setValue('');
    this.inputAgeRegularUseApi.setValue('');
    this.inputTotalPacksApi.setValue('');
    this.startDateInputApi.setValue('');
    this.endDateInputApi.setValue('');
    this.textAreaConsequencesApi.setValue('');
    this.textAreaFamilyAntecedentsApi.setValue('');
    this.fieldStartDateInputApi.setValue('');
    this.fieldEndDateInputApi.setValue('');
  }

  private toggleEnabled(isEnabled: boolean): void {
    this.classificationAutoCompleteAPI.setEnabled(isEnabled);
    this.inputQuantityApi.setEnabled(isEnabled);
    this.inputDescriptiveQuantityApi.setEnabled(isEnabled);
    this.frequencyAutoCompleteAPI.setEnabled(isEnabled);
    this.inputAgeExperienceApi.setEnabled(isEnabled);
    this.inputAgeRegularUseApi.setEnabled(isEnabled);
    this.inputTotalPacksApi.setEnabled(isEnabled);
    this.startDateInputApi.setEnabled(isEnabled);
    this.endDateInputApi.setEnabled(isEnabled);
    this.textAreaConsequencesApi.setEnabled(isEnabled);
    this.textAreaFamilyAntecedentsApi.setEnabled(isEnabled);
    this.frequencyAutoCompleteAPI.setEnabled(isEnabled);
  }

  @OnChange(HealthHistorySocialHistoryCreateAliases.CHECKBOX_DENIES_HABITS)
  handleDeniesHabits(event: ChangeEvent<YesNoType.YES>): void {
    const value = event.value;
    if (value) {
      this.deniesHabits();
      this.toggleEnabled(false);
    } else {
      this.toggleEnabled(true);
    }
  }

  private scaleRadioGroup(expressionId: number, radioGroup: RadioGroupAPI, type: string): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        const responseBody = response.body as SelectableItem[];
        radioGroup.setData(responseBody);
        const socialHistory = this.context.get('socialHistory') as HealthHistorySocialHistoryResponse;
        if (!socialHistory) {
          radioGroup.setValue(responseBody[0]);
          this.formThreeSocialHistoryCreateAPI.setData({ ...this.formThreeSocialHistoryCreateAPI.getData(), [type]: responseBody[0] });
        }
      }).catch((error) => {
        error?.body?.errorMessage && this.showToast(error.body.errorMessage, ToastLevel.ERROR);
      });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'health-history-social-history',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private showModalSubmit(idHealthHistory: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.historyService.back(); },
      onClickOk: () => { this.releaseSocialHistory(idHealthHistory); },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
  }

  private releaseSocialHistory(idHealthHistorySocialHistory: number): void {
    this.formOneSocialHistoryCreateAPI.setLoading(true);
    HttpService.put(Endpoint.RELEASE_HEALTH_HISTORY_SOCIAL_HISTORY, { sequenceNumberSocialHistory: idHealthHistorySocialHistory })
      .then(() => {
        this.formOneSocialHistoryCreateAPI.setLoading(true);
        this.goBack();
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR);
        this.formOneSocialHistoryCreateAPI.setLoading(false)
      });
  }

  private createNewSocialHistory(body: Record<string, Value>): void {
    this.formOneSocialHistoryCreateAPI.setLoading(true);
    HttpService.post(Endpoint.POST_HEALTH_HISTORY_SOCIAL_HISTORY, undefined, { body })
      .then(response => {
        const { idHealthHistory } = response.body as HealthHistorySocialHistoryResponse;
        this.showModalSubmit(idHealthHistory as number);
        this.context.set({ idHealthHistory, isCreated: true });
      })
      .catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => this.formOneSocialHistoryCreateAPI.setLoading(false));
  }

  @OnBeforeSubmit({ target: HealthHistorySocialHistoryCreateAliases.WIZARD })
  handleBeforeSubmit(): void {
    const { encounterId, patientId, idHealthHistory } = this.historyService.getCurrentRouteParams();
    const isCreated = this.context.get('isCreated');

    if (isCreated) {
      this.showModalSubmit(idHealthHistory as number);
      return;
    }

    const formDataOne = this.formOneSocialHistoryCreateAPI.getRecord();
    const formDataTwo = this.formTwoSocialHistoryCreateAPI.getRecord();
    const formDataThree = this.formThreeSocialHistoryCreateAPI.getRecord();
    const deniesHabits = BooleanUtils.isPositiveValue(formDataOne.deniesHabits as boolean);
    const alert = BooleanUtils.isPositiveValue(this.checkboxAlertsAPI.getValue() as boolean);
    const classification = (formDataOne.classification as DomainType)?.valueDomain;
    const type = formDataOne.type as SearchType;
    const brand = formDataOne.brand as SearchType;
    const unitMeasure = formDataOne.unitMeasure as SearchType;
    const amount = formDataTwo.amount || '';
    const descriptiveQuantity = formDataTwo.descriptiveQuantity || '';
    const frequency = (formDataTwo.frequency as DomainType)?.valueDomain;
    const securityLevel = formDataTwo.securityLevel as SearchType;
    const ageExperience = formDataTwo.ageExperience || '';
    const ageRegularUse = formDataTwo.ageRegularUse || '';
    const totalPacks = formDataTwo.totalPacks || '';
    const startDate = ModelService.format('timestamp', formDataThree.startDate);
    const endDate = ModelService.format('timestamp', formDataThree.endDate);
    const problemList = BooleanUtils.isPositiveValue(formDataThree.problemList as boolean);
    const condition = formDataThree.condition as SelectableItem;
    const consequences = formDataThree.consequences || '';
    const antecedents = formDataThree.antecedents || '';
    const notes = formDataThree.notes || '';
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const body = {
      ageExperience,
      ageRegularUse,
      alert,
      amount,
      antecedents,
      attendance: encounterId as number,
      brand: brand && brand.id,
      classification,
      condition: condition.key,
      consequences,
      deniesHabits,
      descriptiveQuantity,
      endDate,
      frequency,
      idHealthHistory: idHealthHistory as number,
      notes,
      physicalPerson: patientId,
      problemList,
      professional: user.physicalPersonId,
      securityLevel: securityLevel && securityLevel.id,
      startDate,
      totalPacks,
      type: type && type.id,
      unitMeasure: unitMeasure && unitMeasure.id,
    } as Record<string, Value>;
    this.createNewSocialHistory(body);
  }

  @OnChange(HealthHistorySocialHistoryCreateAliases.FIELD_CLASSIFICATION_DOMAIN)
  handleClassificationChange(event: ChangeEvent<ClassificationDomain>): void {
    const value = event.value;
    this.typeSearchAPI.setValue(undefined);
    this.typeSearchAPI.setEnabled(false);
    this.brandSearchAPI.setValue(undefined);
    this.brandSearchAPI.setEnabled(false);
    this.unitMeasureSearchAPI.setValue(undefined);
    this.unitMeasureSearchAPI.setEnabled(false);

    if (!!value) {
      this.handleTypeField(String(value.valueDomain))
      this.handleUnitMeasureField(String(value.valueDomain))
    }
  }

  private handleTypeField(classificationDomain: string): void {
    this.typeFetchSearchAPI.setParameters({ classificationDomain })
    const params = { classificationDomain }
    HttpService.get(Endpoint.GET_HEALTH_HISTORY_SOCIAL_HISTORY_TYPES, params)
      .then(response => {
        const body = response.body as Record<string, Value>[]
        this.typeSearchAPI.setEnabled(body.length !== 0);
      })
  }

  private handleUnitMeasureField(classificationDomain: string): void {
    this.unitMeasureFetchSearchAPI.setParameters({ classificationDomain })
    const params = { classificationDomain }
    HttpService.get(Endpoint.GET_HEALTH_HISTORY_SOCIAL_HISTORY_UNIT_MEASURE, params)
      .then(response => {
        const body = response.body as Record<string, Value>[]
        this.unitMeasureSearchAPI.setEnabled(body.length !== 0);
      })
  }

  @OnChange(HealthHistorySocialHistoryCreateAliases.FIELD_TYPE_SEARCH)
  handleTypeChange(event: ChangeEvent<ClassificationDomain>): void {
    const value = event.value;
    if (!!value) {
      this.handleBrandField(Number(value.id))
    }
  }

  private handleBrandField(typeId: number): void {
    this.brandFetchSearchAPI.setParameters({ typeId })
    const params = { typeId }
    HttpService.get(Endpoint.GET_HEALTH_HISTORY_SOCIAL_HISTORY_BRAND, params)
      .then(response => {
        const body = response.body as Record<string, Value>[]
        this.brandSearchAPI.setEnabled(body.length !== 0);
      })
  }
}

export default HealthHistorySocialHistortCreateController;
